/* global Blockly */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Actions} from '../actions';
import {blocklyPrefix, blocklyPostfix} from "../utils";
import postRobot from 'post-robot';
import initBlocklyOverrides from '../blockly'
import Workspace from '../components/Workspace';
import queryString from "query-string";
import SaveModal from "../components/SaveModal";
import CreateJSBlock from "../components/SaveModal/CreateJSBlock";
import DeleteLibraryDialog from "../components/DeleteLibraryDialog";
import SaveBlockTypeErrorDialog from "../components/SaveBlockTypeErrorDialog";
import SaveRESTBlockWarningDialog from "../components/SaveRESTBlockWarningDialog";
import CustomJSModal from '../components/CustomJSModal/CustomJSModal';
import ResizableModal from '../components/PopupModal/Modal';
import sanitizeNoteContent from '../components/PopupModal/noteContentSanitizer';
import {getVersionedBlockName} from '../components/Toolbox/blockVersionConfig';
import {collectBlockSchemas, loadBlockSchemas} from '../components/Toolbox/blockVersions/v3/helpers/blocklySchemaStorage';
import {hasRestAuthenticationDetails, sanitizeRestBlocksForLibrary} from '../utils/restBlockLibrarySanitizer';

export class WorkspaceContainer extends Component {

    constructor(props) {
        super(props);
        this.mountRef = React.createRef();
        this.toolboxRef = React.createRef();
        this.postRobotListeners = [];
        this.pendingTimeouts = [];
        this.blockSaveListener = null;
        this.workspaceValidateListener = null;
        this.selectEnableListener = null;
        this.state = {
            err: {
                msg: '',
                show: false
            },
            currentLibrary: null,
            isInitialized: false,
            restBlockError: false,
            modalContent: '', // Saved content in the popup modal
            modalOpen: false, // Whether the modal is visible or not
            modalChecked: false, // Whether or not the user wants to see the modal on launch
            modalPosition: this._getDefaultModalPosition(), // Position and size of the modal
            customJSBlockId: '',
            customJSBlockCode: '',
            customJSBlockCodeCallback: null,
            customJSBlockMode: 'customJS'
        }
    }

    componentDidMount() {
        this.props.initializeApp();
        // Request Init info only when open workspace
        // Avoid request Init info when open help pages
        if (!this._isBlocklyHelpPage()) {
            this.props.getInitInfo();
        }
        this._initializeBlocklyConfig();

        this._attachListeners();
        this._applyTheme(); // Initial theme setup

        if (this.props.app.context && !this.state.isInitialized) {
            this._scheduleTimeout(() => {
                this._initializeBlockly();
            });
        }
    }

    componentDidUpdate(prevProps) {
        // Apply theme when it changes
        if (prevProps.app.context?.theme?.Name !== this.props.app.context?.theme?.Name) {
            this._applyTheme();
        }

        if (!prevProps.app.context && this.props.app.context && !this.state.isInitialized) {
            this._scheduleTimeout(() => {
                this._initializeBlockly();
            });
        }
    }

    componentWillUnmount() {
        this.pendingTimeouts.forEach(timeoutId => {
            clearTimeout(timeoutId);
        });
        this.pendingTimeouts = [];

        if (this.blockSaveListener) {
            document.removeEventListener("BlockSave", this.blockSaveListener);
            this.blockSaveListener = null;
        }

        if (this.workspace && this.workspaceValidateListener) {
            this.workspace.removeChangeListener(this.workspaceValidateListener);
            this.workspaceValidateListener = null;
        }

        if (globalThis.Blockly?.mainWorkspace && this.selectEnableListener) {
            globalThis.Blockly.mainWorkspace.removeChangeListener(this.selectEnableListener);
            this.selectEnableListener = null;
        }

        this.postRobotListeners.forEach(listener => {
            if (typeof listener === 'function') {
                listener();
                return;
            }
            if (listener?.cancel && typeof listener.cancel === 'function') {
                listener.cancel();
                return;
            }
            if (listener?.close && typeof listener.close === 'function') {
                listener.close();
                return;
            }
            if (listener?.off && typeof listener.off === 'function') {
                listener.off();
            }
        });
        this.postRobotListeners = [];
    }

    _scheduleTimeout(callback, delay = 0) {
        const timeoutId = setTimeout(() => {
            this.pendingTimeouts = this.pendingTimeouts.filter(id => id !== timeoutId);
            callback();
        }, delay);
        this.pendingTimeouts.push(timeoutId);
    }

    _applyTheme() {
        // Update body class for icon fonts and components
        const theme = this.props.app.context?.theme?.Name || 'Dark';
        const bodyClass = theme.toLowerCase() === 'dark' ? 'darkTheme' : 'lightTheme';
        document.body.className = bodyClass;

    }
    
    _isAuthorized() {
        const {debug} = queryString.parse(globalThis.location.search);
        const {isMockData} = this.props.app;
        return !(isMockData && debug !== 'true');
    }

    _attachIframeListeners () {
        const requestOutputListener = postRobot.on('requestOutput', () => {
            // If workspace isn't initialized yet
            if (!this.state.isInitialized || !this.workspace) {
                return { err: 'Blockly workspace not initialized yet' };
            }

            let err = this._validateOutput();
            if (err) {
                // if an error is encountered set the workspace listener to check each event
                // whether the workspace is valid.
                this._setWorkspaceValidateListener();
                return {err}
            }

            const code = this._getBlocklyCode();
            // Check for HTTP links in generated code (only hardcoded URLs, not variables)
            const hasHttpLinks = this._detectHttpLinks(code);
            return {
                code: code,
                properties: this._getProperties(),
                hasHttpLinks: hasHttpLinks
            }
        });
        this.postRobotListeners.push(requestOutputListener);

        // attach listeners for parent window's events
        const undoListener = postRobot.on("blockly:undo", () => this.triggerUndoRedo(false));
        const redoListener = postRobot.on("blockly:redo", () => this.triggerUndoRedo(true));
        this.postRobotListeners.push(undoListener, redoListener);
    }

    /**
     * Detects HTTP (insecure) links in proxy-off REST blocks.
     * EnforceHttps is a device-level setting - only affects direct device calls (proxy off),
     * not calls through the backend proxy (proxy on).
     * 
     * Proxy OFF generates: NetworkApiModule.invokeApi(..., { url: encodeURI("http://...") })
     * Proxy ON generates: BlocklyModule.restBlockCall(requestBody, ...) with requestUrl: in requestBody
     * 
     * @param code The generated Blockly code
     * @returns true if HTTP links are detected in proxy-off blocks, false otherwise
     */
    _detectHttpLinks(code) {
        if (!code || typeof code !== 'string') {
            return false;
        }
        // Strip out the JSDoc comment at the top (contains XML with http:// namespaces)
        const codeWithoutComment = code.replace(/\/\*\*[\s\S]*?\*\//, '');
        const proxyOffHttpRegex = /\burl:\s*encodeURI\(\s*(['"])http:\/\/[^'"]*\1/i;
        return proxyOffHttpRegex.test(codeWithoutComment);
    }

    _attachWorkspaceListeners () {
        this.blockSaveListener = (e) => {
            this.saveMyBlock(e.detail);
        };
        document.addEventListener("BlockSave", this.blockSaveListener);
    }

    _attachListeners() {
        this._attachIframeListeners();
        this._attachWorkspaceListeners();
    }

    _isBlocklyHelpPage() {
        // Check if we're on the Blockly help page
        // Help pages don't need Snap Init Info
        return globalThis.location.pathname === '/docs' || globalThis.location.pathname.startsWith('/docs/');
    }

    _setWorkspaceValidateListener(){
        if (this.workspaceValidateListener) {
            this.workspace.removeChangeListener(this.workspaceValidateListener);
        }
        const onChange = () => {
            let err = this._validateWidgetSelect();
            if (err) {
                this.props.doSendValidatedToParent(false);
            } else {
                this.props.doSendValidatedToParent(true);
                this.workspace.removeChangeListener(onChange);
                if (this.workspaceValidateListener === onChange) {
                    this.workspaceValidateListener = null;
                }
            }
        }
        this.workspaceValidateListener = onChange;
        this.workspace.addChangeListener(onChange);
    }

    _setWarningUntilNextChange(field) {
        field.setWarning(true);
        const closure = field.onChange;
        field.setOnChange((value) => {
            field.setWarning(false);
            field.setOnChange(closure);
            if (closure) {
                closure.call(field, value);
            }
        });
    }

    _initializeBlockly() {
        if (this._isAuthorized()) {
            this._mountBlockly();
            this.setState({isInitialized: true});
            const { tenantId = "-1", userId = "-1", accessToken, snapTenantGuid } = this.props.app.context;
            localStorage.setItem('auth_token', accessToken);

            this.props.getLibraries({
                tenantId: tenantId,
                userId: userId,
                snapTenantGuid: snapTenantGuid
            });
        }
    }

    _stringifyComment(description, tags) {
        // Generate the comment in the standard JSDoc format
        let comment = "/**\n";
    
        if (description) {
            comment += `* ${description}\n*\n`;
        }
    
        tags.forEach(tag => {
            const namePart = tag.name ? ` ${tag.name}` : "";
            comment += `* @${tag.tag}${namePart} ${tag.description}\n`;
        });
    
        comment += "*/";
        return comment;
    }

    _getBlocklyCode(isCopying) {
        this.workspace.isCopying = !!isCopying;
        let js = Blockly.JavaScript.workspaceToCode(this.state.workspace);
        this.workspace.isCopying = false;
        js = js.replaceAll('SnapReturns.', '');
        let xml = Blockly.Xml.workspaceToDom(this.workspace);
        xml = new XMLSerializer().serializeToString(xml);

        // Prepare the comment block content, and generate the comment string manually
        const commentContent = [
            {
                tag: "v1",
                name: "xml",
                description: xml.replaceAll("/*", "!!comment-start!!").replaceAll("*/", "!!comment-end!!")
            }
        ];
        const comment = this._stringifyComment("Blockly Block", commentContent);

        const code = `${comment}\n${blocklyPrefix}\n${js}\n${blocklyPostfix}`;
        return code;
    }

    // provide a JSON describing all additional properties for this blockly session
    _getProperties() {
        const properties = {};

        // add popup settings, which describe how the use is supposed to use this script
        properties.popup = {
            content: sanitizeNoteContent(this.state.modalContent), 
            checked: this.state.modalChecked,
            position: this.state.modalPosition
        };

        // Collect schemas from all active blocks for saving to the server.
        const blockSchemas = collectBlockSchemas(this.workspace);
        if (Object.keys(blockSchemas).length > 0) {
            properties.blockSchemas = blockSchemas;
        }
        
        return properties;
    }

    _stepExecution() {
        if (!globalThis.BlocklyDebuggers) return;
        const debuggr = globalThis.BlocklyDebuggers;
        const block = debuggr.next();
        Blockly.mainWorkspace.highlightBlock(block.value);
    }

    _continueExecution() {
        if (!globalThis.BlocklyDebuggers) return;
        const debuggr = globalThis.BlocklyDebuggers;
        let previousBlock = debuggr.next();
        let block = debuggr.next();
        Blockly.mainWorkspace.highlightBlock(block.value);

        while (!block.done) {
            Blockly.mainWorkspace.highlightBlock(block.value);
            previousBlock = block;
            block = debuggr.next();
        }

        Blockly.mainWorkspace.highlightBlock(previousBlock.value, false);
    }

    _setAllHighlighted(block, highlighted) {
        block.setHighlightBlock(highlighted);
        block.getChildren().forEach((x) => {
            this._setAllHighlighted(x, highlighted)
        })
    }

    _disableDownStreamBlocks(block) {
        if (block === null) {
            return
        }
        block.setEnabled(false);
        if (block.nextConnection?.targetBlock()) {
            this._disableDownStreamBlocks.bind(this)(block.nextConnection.targetBlock());
        }
    }

    _selectEnableBlock() {
        let f = (e) => {
            this.workspace.removeChangeListener(f);
            if (this.selectEnableListener === f) {
                this.selectEnableListener = null;
            }
            let blockId = e.blockId || e.newValue;
            let block = this.workspace.getBlockById(blockId);
            if (block === null) {
                this.workspace.getAllBlocks().forEach(x => {
                    x.setHighlightBlock(false)
                });
                return;
            }
            block = block.getRootBlock();
            this.workspace.removeChangeListener(f);
            if (this.selectEnableListener === f) {
                this.selectEnableListener = null;
            }
            this.workspace.getAllBlocks().forEach(x => {
                x.setHighlightBlock(false)
            });
            this.workspace.getTopBlocks().forEach(this._disableDownStreamBlocks.bind(this));
            block.getDescendants().forEach(x => {
                x.setEnabled(true);
                this.props.doSendValidatedToParent(true);
            });
        }
        Blockly.mainWorkspace.getAllBlocks().forEach(block => {
            if (!block.isShadow()) {
                block.setHighlightBlock(true);
            }
        })
        this.selectEnableListener = f;
        Blockly.mainWorkspace.addChangeListener(f);
    }

    _containsEnabled(block) {
        let next = false;
        if (block.nextConnection?.targetBlock()) {
            next = this._containsEnabled.bind(this)(block.nextConnection.targetBlock())
        }
        return block.isEnabled() || next;
    }

    _validateOutput() {
        let oneBlockError = this._validateOneBlock();
        if (oneBlockError) {
            return oneBlockError;
        }

        let validWidgetsError = this._validateWidgetSelect();
        if (validWidgetsError) {
            return validWidgetsError;
        }

        let validRestBlockError = this.state.restBlockError;
        if (validRestBlockError) {
            this.getRestBlockError(validRestBlockError);
            return validRestBlockError;
        }
        return null;
    }

    _validateWidgetSelect() {
        const disabled = new Set(this.workspace.getAllBlocks()
            .filter(x => !x.isEnabled())
            .flatMap(x => {
                return x.getInternalDescendants().map(y => y.id)
            }));

        let blocks = this.workspace.getAllBlocks().filter(block => {
            return !disabled.has(block.id)
        });

        let err;
        blocks.forEach(block => {
            block.inputList.forEach(input => {
                input.fieldRow.filter(x => {
                    return x instanceof Blockly.FieldDropdown
                }).forEach(ddField => {
                    // Library blocks can retain a widget ID from another app, so validate it
                    // against the current dropdown options before trusting hasBeenSet.
                    if (ddField instanceof Blockly.FieldWidgetsDropdown) {
                        ddField.validate();
                    }

                    if (ddField.hasBeenSet) {
                        return;
                    }
                    // If dd hasn't been set yet, set a warning on that dd, and set the onchange handler
                    // to remove the warning (and reset the previous onchange handler)
                    this._setWarningUntilNextChange(ddField);
                    err = {
                        type:'WidgetSelect',
                        msg:'One or more widget dropdowns were not selected or refer to widgets that no longer exist.'
                    };
                })
            })
        });
        return err || null;
    }

    _validateOneBlock() {
        const topBlocks = this.workspace.getTopBlocks().filter(this._containsEnabled.bind(this));
        if (topBlocks.length <= 1) {
            return null;
        }
        this.workspace.getAllBlocks().filter(x => {
            return x.isEnabled() && (!x.isShadow())
        }).forEach(x => {
            x.setHighlightBlock(true)
        });
        this.workspace.cleanUp();
        this.workspace.zoomToFit();
        this.workspace.getTopBlocks().forEach(x => {
            x.moveBy(50, 50)
        });
        const errMsg = `Blockly scripts must contain only one block structure (the current script has ${topBlocks.length}). Please either select a block or continue editing and delete, connect, or disable (right-click) unconnected blocks.`;
        let dismissError = () => {
            this.setState({err: {msg: errMsg, show: false}});
        }
        this.setState({
            err: {
                msg: errMsg,
                show: true,
                blocking: true,
                buttons: [
                    {
                        text: 'continue editing',
                        callback: () => {
                            Blockly.mainWorkspace.getAllBlocks().forEach(x => this._setAllHighlighted(x, false));
                            dismissError();
                        }
                    },
                    {
                        text: 'select a block',
                        callback: () => {
                            this._selectEnableBlock();
                            dismissError();
                        }
                    }
                ]
            }
        });

        return {
            type:'OneBlockError',
            msg:errMsg
        };
    }

    getRestBlockError(errMessage) {

        this.workspace.zoomToFit();
        const errMsg = errMessage;
        let dismissError = () => {
            this.setState({err: {msg: errMsg, show: false}});
        }
        this.setState({
            err: {
                msg: errMsg,
                show: true,
                blocking: true,
                buttons: [
                    {
                        text: 'continue editing',
                        callback: () => {
                            Blockly.mainWorkspace.getAllBlocks().forEach(x => this._setAllHighlighted(x, false));
                            dismissError();
                        }
                    }
                ]
            }
        });

    }

    setRestBlockError(errMessage) {
        if (this.state.restBlockError != errMessage) {
            this.setState({restBlockError: errMessage});
        }
    }

    // Functions to set props sent to the popup modal
    setContentModal(newContent) {
        this.setState({ modalContent: sanitizeNoteContent(newContent) });
    }

    setOpenModal() {
        this.setState(prevState => ({
            modalOpen: !prevState.modalOpen
        }));
    }

    setCheckModal() {
        this.setState(prevState => ({
            modalChecked: !prevState.modalChecked
        }));
    }

    setPositionModal(newPos) {
        this.setState({ modalPosition: newPos });
    }

    _sendCodeToParent() {
        let err = this._validateOutput();

        if(err){
            this.props.doSendCodeToParent({err});
        } else {
            this.props.doSendCodeToParent({
                code: this._getBlocklyCode(),
                properties: this._getProperties()
            });
        }
    }

    _evalCode() {
        Blockly.JavaScript.init(Blockly.mainWorkspace);
        // NOSONAR: evaluates this app's own Blockly-generated code (from the current
        // workspace's own blocks), not external/attacker-supplied input - this backs
        // the debug-only "Eval" button (gated by actions === "true" in
        // components/Workspace/index.js) used to test-run a script locally in-browser.
        eval(this._getBlocklyCode()); // NOSONAR
    }

    _logCode() {
        console.log(this._getBlocklyCode());
    }

    _getXmlTagContent(code) {
        // Extract the block comment
        const commentMatch = code.match(/\/\*\*([\s\S]*?)\*\//);
        if (!commentMatch) {
            return "";
        }
    
        const commentContent = commentMatch[1].trim();
    
        // Extract the XML content from the comment
        const xmlMatch = commentContent.match(/<xml[\s\S]*?<\/xml>|<xml[^>]*\/>/);
        if (!xmlMatch) {
            return "";
        }
    
        return xmlMatch[0];
    }

    _getCodeFromParent() {
        let {code} = this.props.app.context;
        let xml = this._getXmlTagContent(code);
        xml = xml.replaceAll("!!comment-start!!", "/*").replaceAll("!!comment-end!!", "*/");
        code = code.replaceAll("!!comment-start!!", "/*").replaceAll("!!comment-end!!", "*/");
        return {code, xml}
    }

    // Default position/size for the popup modal, used both as initial state and
    // as the fallback when no saved position exists in the file's properties.
    _getDefaultModalPosition() {
        return {
            x: (globalThis.innerWidth - 500) / 2,
            y: (globalThis.innerHeight - 200) / 10,
            width: 500,
            height: 350
        };
    }

    // Get the saved popup settings (if any) from the js file with the xml of all the blocks
    _getPopupFromFile() {
        const {properties} = this.props.app.context;
        return properties?.popup;
    }

    // Get the boolen variable regarding whether the modal is checked or not from the js file with the xml of all the blocks
    _getBooleanFromFile() {
        return this._getPopupFromFile()?.checked ?? false;
    }

    // Get the content of the popup modal from the js file with the xml of all the blocks
    _getContentFromFile() {
        try {
            return sanitizeNoteContent(this._getPopupFromFile()?.content || '');
        } catch (error) {
            console.error('Error reading file:', error);
            return null;
        }
    }

    // Get the position and size of the popup modal from the js file with the xml of all the blocks
    _getPositionFromFile() {
        return this._getPopupFromFile()?.position ?? this._getDefaultModalPosition();
    }

    // Function to trigger the visibility of the notes modal when already within blockly
    _triggerNotesPopup(open, checked) {
        this.setState(
            {
                modalOpen: open,
                modalChecked: checked,
            }
        );
    }

    // Rules mapping well-known Blockly context-menu entries (matched by their
    // localized text) to the icon/iconHover classes we want to apply to them.
    // Kept as a single table so _applyContextMenuIcons only needs one pass
    // over the options array instead of a separate options.find() per entry.
    _getContextMenuIconRules() {
        return [
            { matches: item => item.text === Blockly.Msg["UNDO"], icon: "snap-icon-undo", iconHover: "snap-icon-undo_dual" },
            { matches: item => item.text === Blockly.Msg["REDO"], icon: "snap-icon-redo", iconHover: "snap-icon-redo_dual" },
            { matches: item => item.text === Blockly.Msg["ADD_COMMENT"], icon: "snap-icon-SMS", iconHover: "snap-icon-SMS_dual" },
            { matches: item => item.text === Blockly.Msg["CLEAN_UP"], icon: "snap-icon-clean", iconHover: "snap-icon-clean_dual" },
            { matches: item => item.text.split(' ')[0] === Blockly.Msg["DELETE_X_BLOCKS"].split(' ')[0], icon: "snap-icon-delete", iconHover: "snap-icon-delete_dual" }
        ];
    }

    // Applies the icon/iconHover from _getContextMenuIconRules to matching
    // context-menu options in a single pass over the array.
    _applyContextMenuIcons(options) {
        const rules = this._getContextMenuIconRules();
        options.forEach(option => {
            const rule = rules.find(r => r.matches(option));
            if (rule) {
                option.icon = rule.icon;
                option.iconHover = rule.iconHover;
            }
        });
    }

    // Copies text to the clipboard, preferring the modern async Clipboard API.
    // Falls back to the legacy execCommand approach when the Clipboard API is
    // unavailable or rejected - this app is embedded cross-origin in an
    // <iframe> by the host (see documentation/HOST_INTEGRATION_CONTRACT.md),
    // and navigator.clipboard.writeText silently fails there unless the host
    // grants the "clipboard-write" Permissions-Policy, which is outside this
    // repo's control.
    _copyTextToClipboard(text) {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).catch(() => {
                this._copyTextViaExecCommand(text);
            });
        } else {
            this._copyTextViaExecCommand(text);
        }
    }

    // Legacy fallback for _copyTextToClipboard - see its comment for why this
    // is still needed alongside the modern Clipboard API.
    _copyTextViaExecCommand(text) {
        const t = document.createElement('textarea');
        t.style.position = 'absolute';
        t.style.left = '100%';
        document.body.appendChild(t);
        t.value = text;
        t.select();
        // NOSONAR: intentional fallback only - see _copyTextToClipboard for why
        // the modern Clipboard API can't fully replace this in all contexts.
        document.execCommand('copy'); // NOSONAR
        t.remove();
    }

    _mountBlockly() {
        initBlocklyOverrides();
        // NOSONAR: evaluates blocklyPrefix, a static build-time string of this app's
        // own hardcoded helper functions (utils/index.js, stringified via toString()),
        // not external/attacker-supplied input - needed to bring these helpers into
        // scope for later Blockly-generated code (see _evalCode/_getBlocklyCode).
        eval(blocklyPrefix); // NOSONAR

        this.workspace = Blockly.inject(this.mountRef.current, {
            toolbox: this.toolboxRef.current,
            media: "/vendor/pxt-blockly/media/",
            grid: {
                spacing: 50,
                length: 51,
                colour: "#555559",
                snap: false
            },
            zoom: {
                controls: true,
                startScale: 0.7,
                maxScale: 1.5,
                minScale: 0.3,
                scaleSpeed: 1.2
            },
            move: {
                wheel: true
            },
            sounds: false,
            renderer: "thrasos"
        });

        // Add this line to properly initialize the JavaScript generator
        Blockly.JavaScript.init(this.workspace);
        this.workspace.configureContextMenu = (options) => {
            // set up global context menu opions (when right-clicked on workspace)
            options.splice(4,2);

            // apply menu icons
            this._applyContextMenuIcons(options);

            // add javascript copy option to context menu
            return options.push({
                text: "Copy JavaScript",
                enabled: true,
                icon: "snap-icon-script",
                iconHover: "snap-icon-script_dual",
                callback: () => {
                    const code = this._getBlocklyCode(true/* copy only */);
                    this._copyTextToClipboard(code);
                }
            }, )
        };

        Blockly.setTheme(Blockly.Themes.SOTI);
        // need to differentiate from other flyouts for onboarding to find the right one
        Blockly.mainWorkspace.getFlyout().svgGroup_.setAttribute("id", "flyout");
        // Load saved schemas before restoring blocks from XML.
        this.workspace.blockSchemas = loadBlockSchemas(this.props.app.context?.properties);
        let xml = this._getCodeFromParent()['xml']
        if (xml !== '') {
            let startBlocks = Blockly.Xml.textToDom(xml);
            Blockly.Xml.domToWorkspace(startBlocks, this.workspace);
            this._scheduleTimeout(() => {
                const allBlocks = this.workspace.getAllBlocks();
                allBlocks?.map(block => {
                    const isDisabled = block.disabled;
                    block.setDisabled(false);
                    block.setDisabled(isDisabled);
                });
            }, 0)
        }
        
        
        this._validateBlocks(this.props.app.context);

        // scroll saved block to center when boot up editor
        this.workspace.markFocused();
        this.workspace.setScale(this.workspace.options.zoomOptions.startScale);
        this.workspace.scrollCenter();

        Blockly.mainWorkspace.registerButtonCallback("createCustomJSBlock", this._createCustomJSBlock.bind(this))
        Blockly.mainWorkspace.registerButtonCallback("showDeleteLibraryDialog", this._showDeleteLibraryDialog.bind(this))

        // Add version-aware block creation method
        // The function will automatically resolve to the correct version based on snapVersion
        // IMPORTANT: Pass BASE block name WITHOUT version suffix!
        // Correct:   workspace.createBlock("rest_service")
        // Incorrect: workspace.createBlock("rest_service_V2")
        this.workspace.createBlock = (baseBlockName) => {
            const versionedBlockName = getVersionedBlockName(baseBlockName, this.props.app.context?.ServerBuildInfo);
            return this.workspace.newBlock(versionedBlockName);
        };

        // modify blockly behaviour based on called action
        if (this.props.app.context?.actionName) {
            switch (this.props.app.context.actionName) {

                case 'tableview_delete_record': {
                    // for tableview delete record action, we should create default REST/delete block
                    if (this.workspace.topBlocks_.length == 0) {
                        const defaultRestBlock = this.workspace.createBlock("rest_service");
                        defaultRestBlock.initTableViewAction("DELETE");
                    }
                    break;
                }

                case 'tableview_edit_record': {
                    // for tableview edit record action, we should create default REST/post block
                    if (this.workspace.topBlocks_.length == 0) {
                        const defaultRestBlock = this.workspace.createBlock("rest_service");
                        defaultRestBlock.initTableViewAction("PUT");
                    }
                    break;
                }
                default:
                    break;
            }
        }

        // Only render the modal if checked is true, so the user wants to popup to be shown on launch
        const shouldShowModal = this._getBooleanFromFile();
        this.setState({
            modalOpen: shouldShowModal,
            modalChecked: shouldShowModal,
            modalContent: this._getContentFromFile(),
            modalPosition: this._getPositionFromFile()
        });
    }

    // Checks if xml contains blocks that references widgets that are not in
    // this Snap app
    _validateBlocks(context) {

        // [{id,name,type,restrictions}]
        const pageWidgets = context.pages.map(x => x.widgets);
        if (context.sliderChildren) {
            pageWidgets.push(context.sliderChildren.map(child => child.widgets));
        }
        // Recursive flatten (not just one level): the sliderChildren entry above is
        // an array of arrays, nested one level deeper than the plain per-page
        // entries, so a single-level .flat() would leave Home Screen Slider
        // header/slides/footer widgets nested and incorrectly treated as missing.
        let widgets = pageWidgets.flat(Infinity);
        
        const ids = new Set(widgets.map(x => {
            return x.id;
        }));

        Blockly.mainWorkspace.getAllBlocks().forEach(block => {
            block.onLoad?.();

            block.inputList.forEach(input => {
                input.fieldRow.forEach(field => {
                    if (field instanceof Blockly.FieldWidgetsDropdown) {
                        // Fix field sizing after XML loading
                        if (field.onLoad) {
                            field.onLoad();
                        }                          
                        if (!ids.has(field.getValue())) {                         
                            // FieldWidgetDropdown references a non-exiting widget Warn on that field, and set change to
                            // remove the warning (b.c. a correct value will then have be chosen) and then set the
                            // onchange to what it was


                            field.setToSelectOption();
                            this._setWarningUntilNextChange(field);
                        }
                    } else if (field instanceof Blockly.FieldDropdown){
                        // Fix regular dropdown field sizing after XML loading
                        if (field.onLoad) {
                            field.onLoad();
                        }
                    }
                })
            })
        });
    }

    saveMyBlock (block) {
        if (!block) { // block is null
            return;
        } else if (!block.previousConnection || !block.nextConnection) { // not a statement block
            this.props.history.push(`/save-block-type-error${this.props.location.search}`);
            return;
        } 
        
        // block can be saved
        const blockDom = Blockly.Xml.blockToDom(block);
        // Check for REST credentials first so the user can be warned before they are removed.
        const shouldWarnAboutCredentials = hasRestAuthenticationDetails(blockDom);
        // Remove credentials from the saved XML copy without changing the blocks in the workspace.
        sanitizeRestBlocksForLibrary(blockDom);
        const blockText = `<xml>${Blockly.Xml.domToText(blockDom)}</xml>`;
        this.setState({ blockToSave: blockText });
        const savePath = shouldWarnAboutCredentials ? '/save-rest-block-warning' : '/save-block';
        this.props.history.push(`${savePath}${this.props.location.search}`);
    }

    _continueRestBlockSave () {
        this.props.history.push(`/save-block${this.props.location.search}`);
    }

    _cancelRestBlockSave () {
        this.setState({ blockToSave: null });
        this.props.history.push(`/${this.props.location.search}`);
    }

    _createCustomJSBlock () {
        this.props.history.push(`/create-js-block${this.props.location.search}`);
    }

    _openJSEditor (blockId, blockCallback, code, mode = 'customJS') {
        this.setState({
            customJSBlockId: blockId,
            customJSBlockCode: code,
            customJSBlockCodeCallback: blockCallback,
            customJSBlockMode: mode
        });
        this.props.history.push(`/custom-js${this.props.location.search}`);
    }

    updateBlockCode(code) {
        if (this.state.customJSBlockCodeCallback) {
            this.state.customJSBlockCodeCallback(code);
        }

        Blockly.mainWorkspace.fireChangeListener({
            type: "JsEditorClose",
            blockId: this.state.customJSBlockId
        });
        this.props.history.push(`/${globalThis.location.search}`);
    }

    _showDeleteLibraryDialog (info) {
        let libraryName = info.svgGroup_.parentElement.getElementsByClassName("blocklyFlyoutLabel")[0].textContent;
        this.setState({currentLibrary: libraryName})
        this.props.history.push(`/delete-library${this.props.location.search}`);
    }

    _initializeBlocklyConfig() {
        /**
         * The richness of block colours, regardless of the hue.
         * Must be in the range of 0 (inclusive) to 1 (exclusive).
         */
        Blockly.HSV_SATURATION = 0.702;

        /**
         * The intensity of block colours, regardless of the hue.
         * Must be in the range of 0 (inclusive) to 1 (exclusive).
         */
        Blockly.HSV_VALUE = 1;


        Blockly.Msg.WIDGET_VALUES_HUE = "#7200e7";
        Blockly.Msg.APP_ACTIONS_HUE = "#32b6e9";
        Blockly.Msg.CONNECTIONS_HUE = "#c5428e";
        Blockly.Msg.ENV_VARIABLES_HUE = "#0fbd8c";
        Blockly.Msg.XSIGHT_TOPICS_HUE = "#41644a";

        Blockly.Msg.LOGIC_HUE = "#33cc33";
        Blockly.Msg.LOOPS_HUE = "#263deb";
        Blockly.Msg.MATH_HUE = "#ff6f00";
        Blockly.Msg.TEXTS_HUE = "#454c50";
        Blockly.Msg.LISTS_HUE = "#cc00ff";
        Blockly.Msg.VARIABLES_HUE = "#cccc00";
        Blockly.Msg.PROCEDURES_HUE = "#006666";
        Blockly.Msg.VARIABLES_DYNAMIC_HUE = "#cccc00";
        Blockly.Msg.COLOUR_HUE = "#666633";
        Blockly.Msg.DATES_HUE = "#006699";
        Blockly.Msg.MYBLOCKS_HUE = "#553982";
        Blockly.Msg.DEBUG_HUE = "#CC0033";
        Blockly.Msg.MOBICONTROL_HUE = "#0074AA";

        Blockly.Themes.SOTI = {};

        Blockly.Themes.SOTI.defaultBlockStyles = {
            "widget_values_blocks": {
                "colourPrimary": Blockly.Msg.WIDGET_VALUES_HUE
            },
            "app_action_blocks": {
                "colourPrimary": Blockly.Msg.APP_ACTIONS_HUE
            },
            "connections_blocks": {
                "colourPrimary": Blockly.Msg.CONNECTIONS_HUE
            },

            "logic_blocks": {
                "colourPrimary": Blockly.Msg.LOGIC_HUE
            },
            "loop_blocks": {
                "colourPrimary": Blockly.Msg.LOOPS_HUE
            },
            "math_blocks": {
                "colourPrimary": Blockly.Msg.MATH_HUE
            },
            "text_blocks": {
                "colourPrimary": Blockly.Msg.TEXTS_HUE
            },
            "list_blocks": {
                "colourPrimary": Blockly.Msg.LISTS_HUE
            },
            "variable_blocks": {
                "colourPrimary": Blockly.Msg.VARIABLES_HUE
            },
            "variable_dynamic_blocks": {
                "colourPrimary": Blockly.Msg.VARIABLES_DYNAMIC_HUE
            },
            "procedure_blocks": {
                "colourPrimary": Blockly.Msg.PROCEDURES_HUE
            },
            "colour_blocks": {
                "colourPrimary": Blockly.Msg.COLOUR_HUE
            },
            "field_blocks": {
                "colourPrimary": Blockly.Colours.textField,
                "colourSecondary": Blockly.Colours.textField,
                "colourTertiary": Blockly.Colours.textField,
            },
            "hat_blocks": {
                "colourPrimary": "330",
                "hat": "cap"
            }
        };

        Blockly.Themes.SOTI.categoryStyles = {
            "widget_values_category": {
                "colour": Blockly.Msg.WIDGET_VALUES_HUE
            },
            "app_action_category": {
                "colour": Blockly.Msg.APP_ACTIONS_HUE
            },
            "connections_category": {
                "colour": Blockly.Msg.CONNECTIONS_HUE
            },

            "logic_category": {
                "colour": Blockly.Msg.LOGIC_HUE
            },
            "loop_category": {
                "colour": Blockly.Msg.LOOPS_HUE
            },
            "math_category": {
                "colour": Blockly.Msg.MATH_HUE
            },
            "text_category": {
                "colour": Blockly.Msg.TEXTS_HUE
            },
            "list_category": {
                "colour": Blockly.Msg.LISTS_HUE
            },
            "variable_category": {
                "colour": Blockly.Msg.VARIABLES_HUE
            },
            "variable_dynamic_category": {
                "colour": Blockly.Msg.VARIABLES_DYNAMIC_HUE
            },
            "procedure_category": {
                "colour": Blockly.Msg.PROCEDURES_HUE
            },
            "colour_category": {
                "colour": Blockly.Msg.COLOUR_HUE
            },
            "field_category": {
                "colour": Blockly.Colours.textField,
                "colourSecondary": Blockly.Colours.textField,
                "colourTertiary": Blockly.Colours.textField,
            }
        };

        Blockly.Themes.SOTI =
            new Blockly.Theme(Blockly.Themes.SOTI.defaultBlockStyles,
                Blockly.Themes.SOTI.categoryStyles);
    }

    getDeepComponent (pathname, libraries) {
        const {isSavingJsBlock} = this.props.block;
        const {userId = "-1", tenantId = "-1", snapTenantGuid } = this.props.app.context

        switch(pathname) {
            case '/save-block':
                return <SaveModal toolbox={this.toolboxRef.current} block={this.state.blockToSave} saveLibrary={this.props.doSaveLibrary} getLibraries={this.props.getLibraries} saveMyBlock={this.props.doSaveMyBlock} mainWorkspace={this.workspace} libraries={libraries} userId={userId} tenantId={tenantId} snapTenantGuid={snapTenantGuid} sendValidatedToParent={this.props.doSendValidatedToParent}/>
            case '/create-js-block':
                return <CreateJSBlock toolbox={this.toolboxRef.current} block={"<xml></xml>"} isSaving={isSavingJsBlock} saveLibrary={this.props.doSaveLibrary} getLibraries={this.props.getLibraries} saveBlock={this.props.doSaveJsBlock} mainWorkspace={this.workspace} libraries={libraries} userId={userId} tenantId={tenantId} snapTenantGuid={snapTenantGuid} sendValidatedToParent={this.props.doSendValidatedToParent}/>
            case '/delete-library':
                return <DeleteLibraryDialog toolbox={this.toolboxRef.current} currentLibrary={this.state.currentLibrary} libraries={libraries} userId={userId} tenantId={tenantId} snapTenantGuid={snapTenantGuid} deleteLibrary={this.props.doDeleteLibrary} history={this.props.history}/>
            case '/save-block-type-error':
                return <SaveBlockTypeErrorDialog history={this.props.history} />
            case '/save-rest-block-warning':
                return <SaveRESTBlockWarningDialog
                    onContinue={this._continueRestBlockSave.bind(this)}
                    onCancel={this._cancelRestBlockSave.bind(this)} />
            case '/custom-js':
                return <CustomJSModal customJSBlockCode={this.state.customJSBlockCode} customJSBlockCodeChange={this.updateBlockCode.bind(this)} mode={this.state.customJSBlockMode}/>
        }
    }

    // play target sound on the server side
    playServerSound(request) {
        this.props.doPlayServerSound(request);
    }

    // trigger undo/redo
    triggerUndoRedo(isRedo) {
        const ws = Blockly.getMainWorkspace();
        if (ws) {
            ws.undo(isRedo);
        }

        // restore focus to Blockly window, so all consequent key pressed will be captured here
        globalThis.focus();
    }

    render() {
        // from props when integrated with backend
        // const myBlocks = [];

        const {myBlocks, jsBlocks, blockLibrary} = this.props.block;

        if (!this.props.app.isInitialized) {
            return null;
        }

        if (!this._isAuthorized()) {
            return <div className="hv-center">
                <p>SOTI Blockly is meant to run on SOTI products only. Please contact SOTI administrator.</p>
            </div>
        }

        return (
            <div className={"workspace-container"}>
                { this.getDeepComponent(globalThis.location.pathname, blockLibrary) }
                <ResizableModal open={this.state.modalOpen} content={this.state.modalContent} setContent={this.setContentModal.bind(this)} setOpen={this.setOpenModal.bind(this)} modalChecked={this.state.modalChecked} setModalChecked={this.setCheckModal.bind(this)} modalPosition={this.state.modalPosition} setModalPosition={this.setPositionModal.bind(this)}/>
                <Workspace {...this.props}
                           myBlocks={myBlocks}
                           jsBlocks={jsBlocks}
                           libraries={blockLibrary}
                           sendCodeToParent={this._sendCodeToParent.bind(this)}
                           evalCode={this._evalCode.bind(this)}
                           logCode={this._logCode.bind(this)}
                           stepExecution={this._stepExecution.bind(this)}
                           continueExecution={this._continueExecution.bind(this)}
                           workspace={this.workspace}
                           mountRef={this.mountRef}
                           modalContent={this.state.modalContent}
                           modalOpen={this.state.modalOpen}
                           modalChecked={this.state.modalChecked}
                           setOpen={this.setOpenModal.bind(this)}
                           toolboxRef={this.toolboxRef}
                           err={this.state.err}
                           saveMyBlock={this.saveMyBlock.bind(this)}
                           ref={(childComponent) => {
                               globalThis.childComponent = childComponent
                           }}
                           deleteBlock={this.props.doDeleteBlock}
                           setRestBlockError = {this.setRestBlockError.bind(this)}
                           openJSEditor={this._openJSEditor.bind(this)}
                           modalPopup={this._triggerNotesPopup.bind(this)}
                           playServerSound = {this.playServerSound.bind(this)}
                ></Workspace>

            </div>
        )
    }
}

WorkspaceContainer.propTypes = {
    // Redux state slices (see reducers/app.js, reducers/block.js)
    app: PropTypes.object.isRequired,
    block: PropTypes.object.isRequired,

    // react-router props
    history: PropTypes.object,
    location: PropTypes.object,

    // Action creators (see actions.js/Actions)
    initializeApp: PropTypes.func,
    getInitInfo: PropTypes.func,
    getLibraries: PropTypes.func,
    doSendCodeToParent: PropTypes.func,
    doSendValidatedToParent: PropTypes.func,
    doSaveMyBlock: PropTypes.func,
    doSaveJsBlock: PropTypes.func,
    doSaveLibrary: PropTypes.func,
    doDeleteLibrary: PropTypes.func,
    doDeleteBlock: PropTypes.func,
    doPlayServerSound: PropTypes.func
};

export default connect(state => state, Actions)(WorkspaceContainer);